// 100 -- Event Venues card. Slots into event-edit page.
//
// Lets editors add the schools and standalone venues that an event will
// play at. The primary venue is the Get-directions fallback for matches
// with no explicit per-match override.

import { useEffect, useState } from "react";
import type { EventVenuePublic, VenuePublic, SchoolSearchResult } from "@dugads/shared";
import * as api from "@/lib/api";
import { ApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// 105 -- added "new" mode so school admins can create an ad-hoc venue
// inline without escalating to super_admin /admin/venues.
type AddPickerKind = "school" | "venue" | "new";

export function EventVenuesCard({
  eventId,
  eventSchoolId,
}: {
  eventId: string;
  eventSchoolId: string;
}) {
  const [rows, setRows] = useState<EventVenuePublic[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Picker state
  const [showAdd, setShowAdd] = useState(false);
  const [kind, setKind] = useState<AddPickerKind>("school");
  const [schoolQuery, setSchoolQuery] = useState("");
  const [schoolOptions, setSchoolOptions] = useState<SchoolSearchResult[]>([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState("");
  const [venues, setVenues] = useState<VenuePublic[]>([]);
  const [selectedVenueId, setSelectedVenueId] = useState("");
  const [label, setLabel] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);
  const [adding, setAdding] = useState(false);

  // 105 -- ad-hoc venue create form state.
  const [newVenueName, setNewVenueName] = useState("");
  const [newVenueLocation, setNewVenueLocation] = useState("");
  const [newVenueLat, setNewVenueLat] = useState("");
  const [newVenueLng, setNewVenueLng] = useState("");

  function reload() {
    api.listEventVenues(eventId)
      .then((r) => setRows(r.event_venues))
      .catch((e) => setError(e instanceof ApiError ? e.message : "Failed to load venues"));
  }
  useEffect(reload, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the picker opens in venue mode, load standalone venue list.
  useEffect(() => {
    if (showAdd && kind === "venue" && venues.length === 0) {
      api.listVenues().then((r) => setVenues(r.venues)).catch(() => {});
    }
  }, [showAdd, kind, venues.length]);

  // Debounced school search when picker is in school mode.
  useEffect(() => {
    if (!showAdd || kind !== "school") return;
    const t = setTimeout(() => {
      api.searchSchools(schoolQuery.trim() || "a", { limit: 20 })
        .then((r) => setSchoolOptions(r.schools))
        .catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [showAdd, kind, schoolQuery]);

  async function handleAdd() {
    if (kind === "school" && !selectedSchoolId) {
      setError("Pick a school"); return;
    }
    if (kind === "venue" && !selectedVenueId) {
      setError("Pick a venue"); return;
    }
    if (kind === "new" && !newVenueName.trim()) {
      setError("Venue name is required"); return;
    }
    // 105 -- guard GPS inputs: reject one-without-the-other so we never end
    // up with half a coordinate (which yields a garbage directions link).
    let parsedLat: number | null = null;
    let parsedLng: number | null = null;
    if (kind === "new") {
      const latRaw = newVenueLat.trim();
      const lngRaw = newVenueLng.trim();
      if ((latRaw && !lngRaw) || (!latRaw && lngRaw)) {
        setError("Provide both latitude and longitude, or leave both blank");
        return;
      }
      if (latRaw) {
        parsedLat = Number(latRaw);
        parsedLng = Number(lngRaw);
        if (!Number.isFinite(parsedLat) || !Number.isFinite(parsedLng)) {
          setError("Latitude and longitude must be numbers"); return;
        }
        if (parsedLat < -90 || parsedLat > 90 || parsedLng < -180 || parsedLng > 180) {
          setError("Coordinates out of range"); return;
        }
      }
    }

    setAdding(true);
    setError(null);
    try {
      let venueIdToAdd = kind === "venue" ? selectedVenueId : null;

      // 105 -- ad-hoc create path: make the Venue first, then attach it as
      // an EventVenue. Two round-trips, but reuses existing endpoints
      // unchanged and keeps the post-create venue visible in /admin/venues.
      if (kind === "new") {
        const created = await api.createVenue({
          name: newVenueName.trim(),
          location: newVenueLocation.trim() || null,
          latitude: parsedLat,
          longitude: parsedLng,
        });
        venueIdToAdd = created.venue.id;
        // Optimistically refresh the standalone venue cache so a follow-up
        // "Standalone venue" tab reflects the new row without a remount.
        setVenues((prev) => [...prev, created.venue]
          .sort((a, b) => a.name.localeCompare(b.name)));
      }

      await api.addEventVenue(eventId, {
        school_id: kind === "school" ? selectedSchoolId : null,
        venue_id: venueIdToAdd,
        label: label.trim() || null,
        is_primary: isPrimary,
      });
      // Reset form
      setSelectedSchoolId(""); setSelectedVenueId(""); setLabel(""); setIsPrimary(false);
      setSchoolQuery("");
      setNewVenueName(""); setNewVenueLocation(""); setNewVenueLat(""); setNewVenueLng("");
      setShowAdd(false);
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to add venue");
    } finally {
      setAdding(false);
    }
  }

  async function handlePromote(eventVenueId: string) {
    try {
      await api.patchEventVenue(eventId, eventVenueId, { is_primary: true });
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to set primary");
    }
  }

  async function handleRemove(eventVenueId: string) {
    if (!confirm("Remove this venue from the event?")) return;
    try {
      await api.deleteEventVenue(eventId, eventVenueId);
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to remove");
    }
  }

  return (
    <Card data-region="event-venues-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle>Venues</CardTitle>
            <p className="text-xs text-muted-foreground mt-1 max-w-md">
              Places this event will be played. The primary venue is used for
              Get-directions unless a match overrides it.
            </p>
          </div>
          <Button size="sm" onClick={() => setShowAdd((s) => !s)} data-action="toggle-add">
            {showAdd ? "Cancel" : "+ Add venue"}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {error && <p className="text-sm text-destructive" data-error>{error}</p>}

        {showAdd && (
          <div className="rounded-md border border-border p-4 space-y-3 bg-muted/30" data-region="add-form">
            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant={kind === "school" ? "default" : "outline"}
                onClick={() => setKind("school")}
              >
                School
              </Button>
              <Button
                size="sm"
                variant={kind === "venue" ? "default" : "outline"}
                onClick={() => setKind("venue")}
              >
                Standalone venue
              </Button>
              <Button
                size="sm"
                variant={kind === "new" ? "default" : "outline"}
                onClick={() => setKind("new")}
                data-action="kind-new"
              >
                + New venue
              </Button>
            </div>

            {kind === "school" ? (
              <>
                <div>
                  <Label htmlFor="ev-school-q">Search schools</Label>
                  <Input
                    id="ev-school-q"
                    value={schoolQuery}
                    onChange={(e) => setSchoolQuery(e.target.value)}
                    placeholder="Type school name…"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto rounded border border-border bg-background">
                  {schoolOptions.length === 0 ? (
                    <p className="p-3 text-sm text-muted-foreground">No matches.</p>
                  ) : (
                    schoolOptions.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setSelectedSchoolId(s.id)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-muted ${
                          selectedSchoolId === s.id ? "bg-primary/10" : ""
                        }`}
                      >
                        {s.display_name ?? s.name}
                        {s.town && <span className="text-muted-foreground"> -- {s.town}</span>}
                      </button>
                    ))
                  )}
                </div>
                {/* Convenience link: host school */}
                {eventSchoolId && !selectedSchoolId && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedSchoolId(eventSchoolId)}
                  >
                    Use host school
                  </Button>
                )}
              </>
            ) : kind === "venue" ? (
              <div>
                <Label htmlFor="ev-venue">Venue</Label>
                <select
                  id="ev-venue"
                  aria-label="Standalone venue"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={selectedVenueId}
                  onChange={(e) => setSelectedVenueId(e.target.value)}
                >
                  <option value="">-- Pick a venue --</option>
                  {venues.map((v) => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Or use <button
                    type="button"
                    className="underline underline-offset-2 hover:text-foreground"
                    onClick={() => setKind("new")}
                  >+ New venue</button> to create one for this event.
                </p>
              </div>
            ) : (
              // 105 -- ad-hoc venue create form. Coordinates optional.
              <div className="space-y-3" data-region="new-venue-form">
                <div>
                  <Label htmlFor="ev-new-name">Venue name</Label>
                  <Input
                    id="ev-new-name"
                    value={newVenueName}
                    onChange={(e) => setNewVenueName(e.target.value)}
                    placeholder='e.g. "Tuks sports grounds"'
                  />
                </div>
                <div>
                  <Label htmlFor="ev-new-location">Address (optional)</Label>
                  <Input
                    id="ev-new-location"
                    value={newVenueLocation}
                    onChange={(e) => setNewVenueLocation(e.target.value)}
                    placeholder="South Street, Hatfield, Pretoria"
                  />
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="ev-new-lat">Latitude (optional)</Label>
                    <Input
                      id="ev-new-lat"
                      value={newVenueLat}
                      onChange={(e) => setNewVenueLat(e.target.value)}
                      placeholder="-25.7545"
                      inputMode="decimal"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ev-new-lng">Longitude (optional)</Label>
                    <Input
                      id="ev-new-lng"
                      value={newVenueLng}
                      onChange={(e) => setNewVenueLng(e.target.value)}
                      placeholder="28.2314"
                      inputMode="decimal"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Without coordinates, the directions button will fall back to a place-name search.
                  This venue will be reusable on other events afterwards.
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="ev-label">Display label (optional)</Label>
              <Input
                id="ev-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder='e.g. "Affies A field"'
              />
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isPrimary}
                onChange={(e) => setIsPrimary(e.target.checked)}
              />
              Set as primary (default directions target)
            </label>

            <div className="flex gap-2">
              <Button onClick={handleAdd} disabled={adding} data-action="add">
                {adding ? "Adding…" : "Add"}
              </Button>
            </div>
          </div>
        )}

        {rows === null ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No venues yet. The event host school will be used as the Get-directions
            target once a venue is added.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((ev) => (
              <li key={ev.id} className="py-3 flex items-start justify-between gap-2" data-item="event-venue" data-item-id={ev.id} data-primary={ev.is_primary ? "true" : "false"}>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{ev.display_name}</span>
                    {ev.is_primary && (
                      <span className="inline-flex items-center rounded bg-primary/15 px-1.5 py-0.5 text-xs text-primary">
                        Primary
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {ev.school_id ? "School" : "Standalone"}
                    </span>
                  </div>
                  {ev.address && <p className="text-xs text-muted-foreground">{ev.address}</p>}
                  {ev.latitude != null && ev.longitude != null && (
                    <p className="text-xs text-muted-foreground">
                      {ev.latitude.toFixed(5)}, {ev.longitude.toFixed(5)}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {!ev.is_primary && (
                    <Button size="sm" variant="outline" onClick={() => handlePromote(ev.id)} data-action="make-primary">
                      Make primary
                    </Button>
                  )}
                  <Button size="sm" variant="destructive" onClick={() => handleRemove(ev.id)} data-action="remove">
                    Remove
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
